import { inject, Injectable } from '@angular/core';

import { Router } from '@angular/router';
import { AgreementsApiConstants } from '@modules/agreements/constants/agreementsApi.constants';
import {
  AgreementsTypes,
  GetAgreementProposal,
  GetAgreementsContract,
  GetContractText,
  SendAgreementsContract,
  SendAgreementsProposal,
  SetAdditionalInfoContract,
  SetAgreementsContract,
  SetAgreementsProposal,
} from '@modules/agreements/store/agreements.action';
import { CloseSmsConfirm } from '@modules/confirmation/store/confirmation.action';
import {
  ContractsRoutingConstants,
  ProposalRoutingConstants,
} from '@modules/order/order.const';
import { ContractsStatus, ProposalStatus } from '@modules/order/order.enum';
import { ScoringRoutingConstants } from '@modules/scoring/constants/scoringRouting.constants';
import { InterceptorService } from '@modules/shared/services/interceptor.service';
import { AppState } from '@modules/shared/store';
import {
  GetConfirmCode,
  HideSpinnerLoading,
  IsHideSpinnerLoading,
  SetSmsResponseCode,
  SetValidationsText,
} from '@modules/shared/store/common.actions';
import { StatusRoutingConstants } from '@modules/status/constants/statusRouting.constants';
import { SetProposalStatus } from '@modules/status/store/status.action';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AgreementsService } from '../services/agreements.service';
import { MatDialog } from '@angular/material/dialog';
import { ContractStatusResponse } from '@codegen';
import { DefaultService } from '@codegen/service';
import { StatusCacheService } from '@modules/shared/services/status-cache.service';

@Injectable()
export class AgreementsEffects {
  private actions$ = inject(Actions);
  private defaultService = inject(DefaultService);
  private statusCacheService = inject(StatusCacheService);
  private agreementsService = inject(AgreementsService);
  private router = inject(Router);
  private store = inject<Store<AppState>>(Store);
  private dialogRef = inject(MatDialog);
  private interceptorService = inject(InterceptorService);

  public GetAgreementsProposal$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAgreementProposal>(AgreementsTypes.GetAgreementProposal),
      map(data => data.payload),
      switchMap(proposalId =>
        this.statusCacheService.getStatus(proposalId).pipe(
          switchMap(response => {
            this.store.dispatch(
              new SetProposalStatus({
                proposalStatus: response.applicationStatus,
                proposalStatusDescription:
                  response.applicationStatusDescription,
                proposalStatusTitle: response.applicationStatusTitle,
                proposalStatusIcon: response.applicationStatusIcon,
                waitingTime: response.waitingTime,
              })
            );
            if (
              response.applicationStatus.key === ProposalStatus.agreementNeed
            ) {
              return this.agreementsService.getProposalAgreements(proposalId);
            } else {
              this.router.navigate([
                `${'/order/' + ProposalRoutingConstants.proposalsList}`,
              ]);
              return EMPTY;
            }
          })
        )
      ),
      switchMap(response => {
        return [
          new SetAgreementsProposal(response.agreements),
          new HideSpinnerLoading(),
        ];
      })
    )
  );

  public SendAgreementsProposal$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SendAgreementsProposal>(AgreementsTypes.SendAgreementsProposal),
      map(data => data.payload),
      switchMap(data => {
        const { applicationId, agreements } = data;
        return this.defaultService
          .agreementsSet({ applicationId, agreements })
          .pipe(
            switchMap(res => {
              if (
                res.errorInfo.error === '10103' ||
                res.errorInfo.error === '10104'
              ) {
                return [
                  new SetSmsResponseCode(res.errorInfo.error),
                  new SetValidationsText(res.errorInfo.errorText),
                ];
              } else if (res.errorInfo.error === '10102') {
                return [
                  new GetConfirmCode({
                    serviceToConfirm:
                      AgreementsApiConstants.setApplicationAgreement,
                    resend: false,
                  }),
                  new HideSpinnerLoading(),
                ];
              } else {
                this.interceptorService.unsubscribeSmsCode();
                this.store.dispatch(new CloseSmsConfirm(true));
                this.store.dispatch(
                  new SetProposalStatus({
                    proposalStatus: res.applicationStatus,
                    proposalStatusDescription: res.applicationStatusDescription,
                    proposalStatusTitle: res.applicationStatusTitle,
                    proposalStatusIcon: res.applicationStatusIcon,
                    waitingTime: res.waitingTime,
                  })
                );
                if (res.waitingTime) {
                  this.router.navigate([
                    `/status/${StatusRoutingConstants.timer}/${applicationId}`,
                  ]);
                  this.dialogRef?.closeAll();
                  return [];
                }
                this.router.navigate([
                  `/scoring/${ScoringRoutingConstants.questionnaire}`,
                  applicationId,
                ]);
                this.dialogRef.closeAll();
                return [];
              }
            })
          );
      })
    )
  );

  public GetAgreementsContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAgreementsContract>(AgreementsTypes.GetAgreementsContract),
      map(data => data.payload),
      switchMap(contractId =>
        this.defaultService.contractsStatus({ contractId }).pipe(
          switchMap((response: ContractStatusResponse) => {
            if (
              response.contractStatus.key === ContractsStatus.waitingSigning
            ) {
              this.store.dispatch(new IsHideSpinnerLoading(false));
              this.store.dispatch(
                new SetAdditionalInfoContract({
                  productTypeCode: response?.productTypeCode,
                  productCategoryCode: response?.productCategoryCode,
                })
              );
              return this.agreementsService
                .getContractAgreement(contractId)
                .pipe(
                  catchError(() => {
                    this.store.dispatch(new HideSpinnerLoading());
                    this.store.dispatch(new IsHideSpinnerLoading(true));
                    return of(null);
                  })
                );
            } else {
              this.router.navigate([
                `${'/order/' + ContractsRoutingConstants.contractsList}`,
              ]);
              return EMPTY;
            }
          })
        )
      ),
      switchMap(response => {
        if (response && response?.errorInfo?.error === '0') {
          return [
            new SetAgreementsContract(response?.documents),
            new HideSpinnerLoading(),
            new IsHideSpinnerLoading(true),
          ];
        } else {
          return [new HideSpinnerLoading(), new IsHideSpinnerLoading(true)];
        }
      })
    )
  );

  public SendAgreementsContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SendAgreementsContract>(AgreementsTypes.SendAgreementsContract),
      map(data => data.payload),
      switchMap(data => {
        const { contractId, documents, confirmationData } = data;
        return this.defaultService
          .contractsSign({ contractId, documents, confirmationData })
          .pipe(
            switchMap(res => {
              if (
                res.errorInfo.error === '10103' ||
                res.errorInfo.error === '10104'
              ) {
                return [
                  new SetSmsResponseCode(res.errorInfo.error),
                  new SetValidationsText(res.errorInfo.errorText),
                ];
              } else if (res.errorInfo.error === '10102') {
                return [
                  new GetConfirmCode({
                    serviceToConfirm:
                      AgreementsApiConstants.setContractAgreement,
                    resend: false,
                  }),
                  new HideSpinnerLoading(),
                ];
              } else {
                this.interceptorService.unsubscribeSmsCode();
                this.store.dispatch(new CloseSmsConfirm(true));
                this.router.navigate([
                  `${'/status/' + StatusRoutingConstants.contractStatus}`,
                  contractId,
                ]);
                this.dialogRef.closeAll();
                return [];
              }
            })
          );
      })
    )
  );

  public GetContractText$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetContractText>(AgreementsTypes.GetContractText),
      map(data => data.payload),
      switchMap(contractId =>
        this.agreementsService.getContractAgreement(contractId)
      ),
      switchMap(data => {
        return [
          new SetAgreementsContract(data.documents),
          new HideSpinnerLoading(),
        ];
      })
    )
  );
}
